// src/public/PublicReasoningTestQuestions.js
import { useState, useEffect, useRef } from 'react';
import { Container, Box, Typography, Alert, Dialog, DialogContent, CircularProgress } from '@mui/material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import useAntiCheat from '../hooks/useAntiCheat';
import WordAssociationTest from './tests/WordAssociationTest';
import NumericalReasoningTest from './tests/NumericalReasoningTest';
import LogicalProcessTest from './tests/LogicalProcessTest';
import SocialContextTest from './tests/SocialContextTest';
import LogicAbstractTest from './tests/LogicAbstractTest';
import IntegrityTest from './tests/IntegrityTest';
import EmotionalIntelligenceTest from './tests/EmotionalIntelligenceTest';

// Sin timeout, un backend colgado deja la request pendiente para siempre.
// Por llamada, no en la instancia, para no afectar las rutas lentas de la app.
const SAVE_TIMEOUT_MS = 15000;

const PublicReasoningTestQuestions = ({
    testId,
    testType,
    language,
    timeRemaining: initialTime,
    onComplete,
    initialResponses,
    initialQuestion,
    applicantEmail
}) => {
    const { t } = useTranslation();
    const [currentQuestion, setCurrentQuestion] = useState(initialQuestion || 0);
    const [responses, setResponses] = useState(initialResponses || {});
    const [timeRemaining, setTimeRemaining] = useState(initialTime);

    // Momento exacto en que vence el test, anclado al reloj. El conteo NO
    // resta 1 por tick: los navegadores estrangulan los timers de pestanas
    // en segundo plano (hasta un tick por minuto), y restando por tick el
    // cronometro se pausaba al cambiar de pestana, estirando el tiempo real
    // del candidato. Calculando contra el reloj, al volver (o en el propio
    // tick estrangulado) el conteo se corrige solo y dispara el cierre si
    // ya vencio.
    const deadlineRef = useRef(Date.now() + (initialTime || 0) * 1000);
    const [saving, setSaving] = useState(false);
    const [showCompletingModal, setShowCompletingModal] = useState(false);
    const [error, setError] = useState('');
    const timerRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    // Ref que siempre apunta a la versión más reciente de handleAutoComplete.
    // El setInterval del timer se crea una sola vez al montar y su closure
    // queda congelado en el primer render: llamar handleAutoComplete directo
    // usaba `responses`/`currentQuestion` iniciales, por lo que al expirar el
    // tiempo las respuestas de la página en curso nunca se guardaban.
    // Se reasigna en cada render (ver línea tras la definición del handler).
    const handleAutoCompleteRef = useRef(null);

    // Inicializar hook anti-trampa
    const { getEventsSummary, resetEvents } = useAntiCheat({
        enabled: true,
        userId: applicantEmail,
        testId: testId
    });

    // Ref para trackear qué páginas ya se guardaron
    const savedPagesRef = useRef(new Set());

    // Páginas para las que YA se envió una vez el resumen anti-trampa. En un
    // reintento se manda `antiCheatEvents: null`, porque el backend acumula esos
    // eventos con push sin deduplicar y `completeTest` recalcula el índice de
    // confiabilidad sobre ese arreglo al cerrar: reenviarlos movería el índice.
    // No se puede saber desde el cliente si el primer intento alcanzó a escribir.
    const attemptedPagesRef = useRef(new Set());

    // Timer countdown (anclado al reloj, no al numero de ticks)
    useEffect(() => {
        timerRef.current = setInterval(() => {
            const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
            setTimeRemaining(remaining);

            if (remaining <= 0) {
                clearInterval(timerRef.current);
                // Usar el ref para invocar la version vigente del handler
                handleAutoCompleteRef.current?.();
            }
        }, 1000);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    // Cleanup timeouts
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, []);

    /**
     * Guarda la página en el backend y DEVUELVE si lo logró.
     *
     * Antes se tragaba el error y no devolvía nada, así que quien llamaba marcaba
     * la página como guardada aunque la request se hubiera caído.
     */
    const savePageToBackend = async (pageResponses, currentQ, antiCheatEvents = null) => {
        try {
            await api.put(
                `/reasoning-tests-public/${testId}/save-page-responses`,
                {
                    applicantEmail,
                    responses: pageResponses,
                    currentQuestion: currentQ,
                    antiCheatEvents: antiCheatEvents
                },
                { timeout: SAVE_TIMEOUT_MS }
            );
            return true;
        } catch (err) {
            console.error('Error saving page responses:', err);
            return false;
        }
    };

    /**
     * Envía la página actual si aún no está confirmada.
     *
     * El resumen anti-trampa solo viaja en el PRIMER intento de cada página (ver
     * attemptedPagesRef). Y `resetEvents` solo corre si el guardado funcionó: si
     * falló, los eventos quedan en el hook en vez de descartarse.
     */
    const persistCurrentPage = async (pageResponses, page) => {
        if (savedPagesRef.current.has(page)) return true;

        const firstAttempt = !attemptedPagesRef.current.has(page);
        const pageEvents = firstAttempt ? getEventsSummary() : null;
        attemptedPagesRef.current.add(page);

        const ok = await savePageToBackend(pageResponses, page, pageEvents);

        if (ok) {
            savedPagesRef.current.add(page);
            if (firstAttempt) resetEvents();
        }

        return ok;
    };

    const handleAnswerChange = (questionId, answer) => {
        const newResponses = { ...responses, [questionId]: answer };
        setResponses(newResponses);
    };

    const saveCurrentResponses = async () => {
        setSaving(true);
        try {
            await persistCurrentPage(responses, currentQuestion);
        } finally {
            setSaving(false);
        }
    };

    const handleQuestionChange = async (newQuestion, responsesToSave = null) => {
        setSaving(true);
        try {
            const finalResponses = responsesToSave !== null ? responsesToSave : responses;

            // Guardar la página ACTUAL (antes de cambiar) solo si no se confirmó antes
            await persistCurrentPage(finalResponses, currentQuestion);

            if (responsesToSave !== null) {
                setResponses(finalResponses);
            }

            setCurrentQuestion(newQuestion);

            await api.put(
                `/reasoning-tests-public/${testId}/current-question`,
                {
                    applicantEmail,
                    currentQuestion: newQuestion
                }
            );
        } catch (error) {
            console.error('Error updating current question:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = async (responsesToSave = null) => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        setSaving(true);
        setShowCompletingModal(true);

        try {
            const finalResponses = responsesToSave !== null ? responsesToSave : responses;

            // Guardar la última página solo si no se confirmó antes (race condition)
            await persistCurrentPage(finalResponses, currentQuestion);

            if (responsesToSave !== null) {
                setResponses(finalResponses);
            }

            // Las respuestas viajan al backend: si a alguna página le falló su
            // guardado individual, se recupera acá.
            const ok = await onComplete(finalResponses);

            if (!ok) {
                // Sin esto el modal quedaba girando para siempre
                setShowCompletingModal(false);
                setError(t('reasoningTests.errorCompletingTest'));
                if (timerRef.current) clearInterval(timerRef.current);
            }
        } catch (err) {
            console.error('Error completing test:', err);
            setShowCompletingModal(false);
            setError(t('reasoningTests.errorCompletingTest'));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Cierre automático al agotarse el cronómetro.
     *
     * Si el envío falla, el padre igual saca al candidato de las preguntas y le
     * muestra un mensaje distinto: su tiempo ya terminó y no puede seguir
     * respondiendo, pero tampoco se le puede asegurar que quedó registrado.
     */
    const handleAutoComplete = async () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        setShowCompletingModal(true);

        // Guardar respuestas pendientes antes de completar
        await saveCurrentResponses();

        await onComplete(responses, { expiredByTime: true });
    };

    // Reasignar en cada render para que el interval del timer siempre ejecute
    // la versión vigente del handler (con responses/currentQuestion actuales)
    handleAutoCompleteRef.current = handleAutoComplete;

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const renderTestComponent = () => {
        const commonProps = {
            responses,
            onAnswerChange: handleAnswerChange,
            currentQuestion,
            onQuestionChange: handleQuestionChange,
            onComplete: handleComplete,
            saving,
            language,
            timeRemaining
        };

        switch (testType) {
            case 'wordAssociation':
                return <WordAssociationTest {...commonProps} />;
            case 'numericalReasoning':
                return <NumericalReasoningTest {...commonProps} />;
            case 'logicalProcess':
                return <LogicalProcessTest {...commonProps} />;
            case 'socialContext':
                return <SocialContextTest {...commonProps} />;
            case 'logicalAbstract':
                return <LogicAbstractTest {...commonProps} />;
            case 'integrityTest':
                return <IntegrityTest {...commonProps} />;
            case 'emotionalIntelligence':
                return <EmotionalIntelligenceTest {...commonProps} />;
            default:
                return null;
        }
    };

    const getTestTitle = () => {
        switch (testType) {
            case 'wordAssociation':
                return t('reasoningTests.wordAssociation');
            case 'numericalReasoning':
                return t('reasoningTests.numericalReasoning');
            case 'logicalProcess':
                return t('reasoningTests.logicalProcess');
            case 'socialContext':
                return t('reasoningTests.socialContext');
            case 'logicalAbstract':
                return t('reasoningTests.logicalAbstract');
            case 'integrityTest':
                return t('reasoningTests.integrityTest');
            case 'emotionalIntelligence':
                return t('reasoningTests.emotionalIntelligence');
            default:
                return t('reasoningTests.test');
        }
    };

    return (
        <>
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" gutterBottom align="center">
                        {getTestTitle()}
                    </Typography>
                    {timeRemaining !== null && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 1,
                                mt: 2
                            }}
                        >
                            <Typography
                                variant="h6"
                                color={timeRemaining < 60 ? 'error' : 'primary'}
                            >
                                {t('reasoningTests.timeRemaining')}: {formatTime(timeRemaining)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {renderTestComponent()}
            </Container>

            {/* Modal de completando test */}
            <Dialog
                open={showCompletingModal}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(5px)',
                    }
                }}
                slotProps={{
                    backdrop: {
                        style: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        }
                    }
                }}
            >
                <DialogContent sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={60} sx={{ mb: 3 }} />
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {t('publicEvaluations.completingEvaluation')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('publicEvaluations.processingResponses')}
                        <br />
                        {t('publicEvaluations.operationTime')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                        {t('publicEvaluations.doNotClose')}
                    </Typography>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PublicReasoningTestQuestions;